import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useRecurly } from '@recurly/react-recurly';
import queryString from 'query-string';

import {
  Box,
  Flex,
  Text,
  Button,
  Heading,
  Skeleton,
  FormControl,
  AlertDialog,
  AlertDialogBody,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/core';

import axios from '../../utils/axios';
import useScrollToTop from '../../hooks/useScrollToTop';
import useToastMod from '../../hooks/useToastMod';

import handleKeyPress from '../../utils/keypress';

import _init from './_init';
import BillingInfo from './BillingInfo';
import { Environments } from '../../config/config.types';

export default function Upgrade() {
  const [state, setState] = useState(_init);
  const [loading, setLoading] = useState(false);

  const { refEl } = useScrollToTop();

  // refs
  const buttonRef = useRef(null);
  const cancelRef = useRef(null);
  const formRef = useRef<HTMLFormElement>(null);

  // access recurly instance
  const recurly = useRecurly();

  // toast some bread
  const toast = useToastMod();

  // access history object
  const history = useHistory();

  const environment =
    (process.env.REACT_APP_ENV as Environments) || Environments.development;

  useEffect(() => {
    const getUser = async () => {
      setState((prevState) => ({ ...prevState, requesting: true }));
      try {
        const {
          data: { data },
          status,
        } = await axios.get('accounts');
        if (status === 200) {
          return setState((prevState) => ({
            ...prevState,
            requesting: false,
            isLoaded: true,
            accountExists: true,
            fname: data[0].first_name,
            lname: data[0].last_name,
            address: data[0].street,
            city: data[0].city,
            sorp: data[0].state,
            country: '',
          }));
        }
      } catch (err) {
        return setState((prevState) => ({
          ...prevState,
          requesting: false,
          dialog: true,
        }));
      }
    };
    getUser();
    // eslint-disable-next-line
  }, []);

  // handlers
  const changeHandler = (e: any) => {
    e.preventDefault();
    setState({ ...state, [e.target.name]: e.target.value });
  };

  // membership choice handler
  const chooseMem = (e: any, plan: string) => {
    e.preventDefault();
    setState({ ...state, plan: plan, isOpen: false });
  };

  // payment type handler
  const choosePay = (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
    e.preventDefault();
    if (state.paymentType === type) {
      setState({ ...state, paymentType: '' });
    } else {
      setState({ ...state, paymentType: type });
    }
  };

  // close alert dialog
  const closeAlert = () => {
    setState((prevState) => ({
      ...prevState,
      dialog: false,
    }));
    return history.push({ pathname: '/library' });
  };

  const handlePayPal = async (token_id: string) => {
    const payload = {
      token_id,
      plan_type: state.plan,
      payment_method: state.paymentType,
    };
    axios
      .post('subscriptions/upgrade', payload)
      .then(({ data: { message, data } }) => {
        setLoading(false);
        toast({
          title: `${message}`,
          description: `Voila ${data[0].account.firstName}!, your subscription has been created.`,
          status: 'success',
          duration: 10000,
          position: 'top',
          isClosable: true,
        });
        // Track subscription with react pixel track
        if (environment === 'production') {
          try {
            //@ts-ignore
            window.fbq('track', 'Purchase', {
              value: data[0].unitAmount,
              currency: data[0].currency,
            });
          } catch (err) {}
        }
        // Push to confirmation page
        const params = {
          plan: data[0].plan.name,
          amount: data[0].unitAmount,
          uno: data[0].currentTermStartedAt,
          ...(data[0].couponRedemptions && {
            coupon: true,
            coupon_name: data[0].couponRedemptions[0].coupon.name,
            coupon_code: data[0].couponRedemptions[0].coupon.code,
          }),
          upgrade: true,
        };
        // Push to confirmation page
        history.push({
          pathname: '/confirmation',
          search: `?${queryString.stringify(params)}`,
        });
      })
      .catch(
        ({
          response: {
            data: { message },
          },
        }) => {
          setLoading(false);
          return toast({
            title: 'Something went wrong!',
            description: message || '',
            status: 'error',
            duration: 10000,
            position: 'top',
            isClosable: true,
          });
        },
      );
  };

  const submitHandler = async (e: any) => {
    e.preventDefault();

    try {
      setState({ ...state, requesting: true });
      // @ts-ignore
      recurly.token(formRef.current, (err, token) => {
        if (err) {
          setState((prevState) => ({
            ...prevState,
            requesting: false,
          }));
          // handle error
          return toast({
            title: 'An error occured',
            description: `${err.message}`,
            status: 'error',
            duration: 10000,
            position: 'top',
            isClosable: true,
          });
        } else {
          const payload = {
            token_id: token.id,
            postal_code: state.zip,
            plan_type: state.plan,
            payment_method: state.paymentType,
          };
          axios
            .post('subscriptions/upgrade', payload)
            .then(({ data: { message, data } }) => {
              setState({ ...state, requesting: false });
              toast({
                title: `${message}`,
                description: `Voila ${data[0].account.firstName}!, your subscription has been created.`,
                status: 'success',
                duration: 10000,
                position: 'top',
                isClosable: true,
              });
              if (environment === 'production') {
                // Track subscription with react pixel track
                try {
                  //@ts-ignore
                  window.fbq('track', 'Purchase', {
                    value: data[0].unitAmount,
                    currency: data[0].currency,
                  });
                } catch (err) {}
              }
              const params = {
                plan: data[0].plan.name,
                amount: data[0].unitAmount,
                uno: data[0].currentTermStartedAt,
                ...(data[0].couponRedemptions && {
                  coupon: true,
                  coupon_name: data[0].couponRedemptions[0].coupon.name,
                  coupon_code: data[0].couponRedemptions[0].coupon.code,
                }),
                upgrade: true,
              };
              // Push to confirmation page
              history.push({
                pathname: '/confirmation',
                search: `?${queryString.stringify(params)}`,
              });
            })
            .catch(
              ({
                response: {
                  data: { message },
                },
              }) => {
                setState({ ...state, requesting: false });
                return toast({
                  title: 'An error occurred',
                  description: message || '',
                  status: 'error',
                  duration: 10000,
                  position: 'top',
                  isClosable: true,
                });
              },
            );
        }
      });
    } catch (err) {
      setState({ ...state, requesting: false });
      return toast({
        title: err.message,
        description:
          'Unable to verify your request. Please refresh this page and try again',
        status: 'error',
        duration: 10000,
        position: 'top',
        isClosable: true,
      });
    }
  };

  return (
    <Box minHeight={{ sm: 'calc(100vh - 18.25rem)' }}>
      <Flex justifyContent={'center'} background={'white'} ref={refEl}>
        <Flex
          width={1200}
          px={['1.5rem', '1.5rem', '0.94rem']}
          flexDirection={'column'}
          textAlign={'center'}
          paddingTop={127}
          paddingBottom={50}
        >
          <Heading
            as="h3"
            size="xl"
            color="brand.secondary"
            letterSpacing={'0.01em'}
          >
            Upgrade your Readeo Account
          </Heading>
          <Text marginTop={5} fontSize={16} letterSpacing={'0.02em'}>
            Please choose a membership type to upgrade your account.
          </Text>
        </Flex>
      </Flex>
      <Flex
        justifyContent={'center'}
        background={
          'linear-gradient(0deg, rgba(131, 208, 228, 0.5), rgba(131, 208, 228, 0.5)), #FFFFFF'
        }
      >
        <Flex width={420} px="0.938rem">
          <Skeleton isLoaded={state.isLoaded}>
            <FormControl
              as="form"
              width="100%"
              my={30}
              display="flex"
              flexDirection="column"
              ref={formRef}
              onKeyPress={(e) => handleKeyPress(e, buttonRef, submitHandler)}
            >
              <BillingInfo
                state={state}
                changeHandler={changeHandler}
                chooseMem={chooseMem}
                choosePay={choosePay}
                loading={loading}
                setLoading={setLoading}
                buttonRef={buttonRef}
                handlePayPal={handlePayPal}
                submitHandler={submitHandler}
              />
              <AlertDialog
                isOpen={state.dialog}
                leastDestructiveRef={cancelRef}
                onClose={closeAlert}
              >
                <AlertDialogOverlay />
                <AlertDialogContent>
                  <AlertDialogHeader fontSize="lg" fontWeight="bold">
                    Oops! Non-existent Account
                  </AlertDialogHeader>
                  <AlertDialogBody>
                    There seems to be an issue with that account code.
                  </AlertDialogBody>
                  <AlertDialogFooter>
                    <Button variantColor="red" onClick={closeAlert} ml={3}>
                      Close
                    </Button>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </FormControl>
          </Skeleton>
        </Flex>
      </Flex>
    </Box>
  );
}
